import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/AppContext";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import SalesChartPanel from "./Statistics/SalesChartPanel";

const colors = {
  primary: "#1565C0",
  dark: "#0D47A1",
  medium: "#4D8FD8",
  pastel1: "#8DB9EC",
  pastel2: "#BCD6F4",
  pastel3: "#D7E7F8",
  light: "#EAF2FD",
  background: "#F4F7FB",
  border: "#D5E1EF",
  text: "#172033",
  muted: "#64748B",
  danger: "#C62828",
  dangerLight: "#FDECEC",
};

const money = (value) => {
  return new Intl.NumberFormat("es-EC", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
};

const number = (value) => {
  return new Intl.NumberFormat("es-EC", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
};

const SummaryCard = ({ title, value, secondary, danger = false }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        p: 2.25,
        borderRadius: 2.5,
        borderColor: danger ? "#F2B8B5" : colors.border,
        borderTop: `4px solid ${danger ? colors.danger : colors.primary}`,
        bgcolor: danger ? colors.dangerLight : "#fff",
      }}
    >
      <Typography
        sx={{
          color: danger ? colors.danger : colors.muted,
          fontSize: 12.5,
          fontWeight: 700,
          mb: 0.75,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          color: danger ? colors.danger : colors.text,
          fontSize: { xs: 22, md: 25 },
          fontWeight: 800,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>

      {secondary && (
        <Typography
          sx={{
            color: danger ? colors.danger : colors.muted,
            fontSize: 12,
            fontWeight: 500,
            mt: 0.75,
          }}
        >
          {secondary}
        </Typography>
      )}
    </Paper>
  );
};

const SectionTitle = ({ children }) => {
  return (
    <Typography
      sx={{
        color: colors.dark,
        fontSize: 16,
        fontWeight: 800,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
};

const CatalogDonut = ({ products, services }) => {
  const productValue = Number(products || 0);
  const serviceValue = Number(services || 0);
  const total = productValue + serviceValue;

  if (total <= 0) {
    return null;
  }

  const productPercent = (productValue / total) * 100;

  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        p: 2.25,
        borderRadius: 2.5,
        borderColor: colors.border,
        bgcolor: "#fff",
      }}
    >
      <SectionTitle>Catálogo activo</SectionTitle>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          flexWrap: "wrap",
          minHeight: 210,
        }}
      >
        <Box
          sx={{
            width: 150,
            height: 150,
            borderRadius: "50%",
            background: `conic-gradient(${colors.pastel1} 0% ${productPercent}%, ${colors.pastel2} ${productPercent}% 100%)`,
            position: "relative",
            flexShrink: 0,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 31,
              borderRadius: "50%",
              bgcolor: "#fff",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                color: colors.dark,
                fontSize: 25,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              {number(total)}
            </Typography>

            <Typography
              sx={{
                color: colors.muted,
                fontSize: 11,
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              Registros
            </Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: 145 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                bgcolor: colors.pastel1,
              }}
            />

            <Box>
              <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 700 }}>
                Productos
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 12 }}>
                {number(productValue)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 13,
                height: 13,
                borderRadius: "50%",
                bgcolor: colors.pastel2,
              }}
            />

            <Box>
              <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 700 }}>
                Servicios
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 12 }}>
                {number(serviceValue)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const BestSellersBars = ({ items }) => {
  const validItems = items.filter((item) => Number(item.total_neto || 0) > 0);

  if (!validItems.length) {
    return null;
  }

  const maximum = Math.max(
    ...validItems.map((item) => Number(item.total_neto || 0)),
    1
  );

  const palette = [
    colors.primary,
    colors.medium,
    colors.pastel1,
    "#75A7E1",
    "#9BC1EC",
    "#B6D2F1",
    "#6F9FD6",
    "#A8C9EE",
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        height: "100%",
        p: 2.25,
        borderRadius: 2.5,
        borderColor: colors.border,
        bgcolor: "#fff",
      }}
    >
      <SectionTitle>Más vendidos</SectionTitle>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.7 }}>
        {validItems.map((item, index) => {
          const width = Math.max(
            (Number(item.total_neto || 0) / maximum) * 100,
            4
          );

          return (
            <Box key={`${item.tipo}-${item.codigo}-${index}`}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.6,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      color: colors.text,
                      fontSize: 12.5,
                      fontWeight: 700,
                    }}
                  >
                    {item.descripcion}
                  </Typography>

                  <Typography
                    sx={{
                      color: colors.muted,
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                  >
                    {item.tipo} · {number(item.cantidad)}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: colors.dark,
                    fontSize: 12.5,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {money(item.total_neto)}
                </Typography>
              </Box>

              <Box
                sx={{
                  height: 9,
                  bgcolor: colors.light,
                  borderRadius: 5,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${width}%`,
                    height: "100%",
                    bgcolor: palette[index % palette.length],
                    borderRadius: 5,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const AdminStatisticsContent = () => {
  const { accessToken } = useContext(AppContext);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDashboard = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const currentYear = new Date().getFullYear();

        const response = await fetch(
          `/dashboard/resumen/?periodo=anio&anio=${currentYear}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          setDashboard(null);
          setError(
            data?.error ||
              "No se pudo cargar la información del dashboard."
          );
          return;
        }

        setDashboard(data);
      } catch {
        setDashboard(null);
        setError("No se pudo conectar con el servidor.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [accessToken]);

  const bestSellers = useMemo(() => {
    if (!dashboard) {
      return [];
    }

    const products = (dashboard.productos_mas_vendidos || []).map((item) => ({
      ...item,
      tipo: "Producto",
    }));

    const services = (dashboard.servicios_mas_vendidos || []).map((item) => ({
      ...item,
      tipo: "Servicio",
    }));

    return [...products, ...services]
      .filter((item) => Number(item.total_neto || 0) > 0)
      .sort(
        (a, b) =>
          Number(b.total_neto || 0) -
          Number(a.total_neto || 0)
      )
      .slice(0, 8);
  }, [dashboard]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 420,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={34} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  const ventas = dashboard?.ventas || {};
  const compras = dashboard?.compras || {};
  const personas = dashboard?.personas || {};
  const catalogo = dashboard?.catalogo || {};
  const inventario = dashboard?.inventario || {};
  const topClientes = dashboard?.clientes_mayores_compradores || [];
  const alertasInventario = inventario.alertas || [];

  const totalAlertas =
    Number(inventario.registros_agotados || 0) +
    Number(inventario.registros_stock_bajo || 0);

  return (
    <Box
      sx={{
        width: "95%",
        mx: "auto",
        mt: { xs: 2, sm: 3 },
        mb: 5,
      }}
    >
      <Typography
        sx={{
          color: colors.dark,
          fontSize: { xs: 22, md: 25 },
          fontWeight: 800,
          mb: 2.5,
        }}
      >
        Dashboard
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            border: "1px solid #F2B8B5",
          }}
        >
          {error}
        </Alert>
      )}

      {dashboard && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title="VENTAS DEL AÑO"
              value={money(ventas.ventas_netas)}
              secondary={`${ventas.facturas_autorizadas || 0} facturas`}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title="POR COBRAR"
              value={money(ventas.saldo_por_cobrar)}
              secondary={`Ticket promedio ${money(ventas.ticket_promedio)}`}
              danger={Number(ventas.saldo_por_cobrar || 0) > 0}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title="COMPRAS DEL AÑO"
              value={money(compras.total_compras)}
              secondary={`Por pagar ${money(compras.saldo_por_pagar)}`}
              danger={Number(compras.saldo_por_pagar || 0) > 0}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <SummaryCard
              title="ALERTAS DE INVENTARIO"
              value={number(totalAlertas)}
              secondary={`${inventario.registros_agotados || 0} agotados · ${inventario.registros_stock_bajo || 0} bajos`}
              danger={totalAlertas > 0}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                borderColor: colors.border,
                bgcolor: "#fff",
                px: { xs: 1.5, md: 2.5 },
                py: 1.75,
              }}
            >
              <Grid container>
                {[
                  { label: "Clientes", value: personas.clientes },
                  { label: "Proveedores", value: personas.proveedores },
                  { label: "Productos activos", value: catalogo.productos_activos },
                  { label: "Servicios activos", value: catalogo.servicios_activos },
                  { label: "Bodegas", value: catalogo.bodegas_activas },
                ].map((item, index) => (
                  <Grid item xs={6} sm={4} md key={item.label}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRight: {
                          xs: "none",
                          md:
                            index < 4
                              ? `1px solid ${colors.border}`
                              : "none",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          color: colors.muted,
                          fontSize: 11.5,
                          fontWeight: 600,
                        }}
                      >
                        {item.label}
                      </Typography>

                      <Typography
                        sx={{
                          color: colors.dark,
                          fontSize: 20,
                          fontWeight: 800,
                          mt: 0.2,
                        }}
                      >
                        {number(item.value)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <SalesChartPanel accessToken={accessToken} />
          </Grid>

          {(Number(catalogo.productos_activos || 0) > 0 ||
            Number(catalogo.servicios_activos || 0) > 0) && (
            <Grid item xs={12} lg={4}>
              <CatalogDonut
                products={catalogo.productos_activos}
                services={catalogo.servicios_activos}
              />
            </Grid>
          )}

          {bestSellers.length > 0 && (
            <Grid item xs={12} lg={8}>
              <BestSellersBars items={bestSellers} />
            </Grid>
          )}

          {topClientes.length > 0 && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 2.5,
                  borderColor: colors.border,
                  bgcolor: "#fff",
                }}
              >
                <SectionTitle>Clientes con mayores compras</SectionTitle>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors.primary }}>
                        <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                          Cliente
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff", fontWeight: 700 }}>
                          Documentos
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: 700 }}>
                          Compras
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: 700 }}>
                          Saldo
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {topClientes.map((cliente, index) => (
                        <TableRow key={`${cliente.identificacion}-${index}`}>
                          <TableCell>
                            <Typography
                              sx={{
                                color: colors.text,
                                fontSize: 13,
                                fontWeight: 700,
                              }}
                            >
                              {cliente.razon_social}
                            </Typography>

                            <Typography
                              sx={{
                                color: colors.muted,
                                fontSize: 11.5,
                              }}
                            >
                              {cliente.identificacion}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {cliente.documentos}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              color: colors.dark,
                              fontWeight: 700,
                            }}
                          >
                            {money(cliente.total_neto)}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              color:
                                Number(cliente.saldo || 0) > 0
                                  ? colors.danger
                                  : colors.muted,
                              fontWeight: 700,
                            }}
                          >
                            {money(cliente.saldo)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}

          {alertasInventario.length > 0 && (
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 2.5,
                  borderColor: colors.border,
                  bgcolor: "#fff",
                }}
              >
                <SectionTitle>Alertas de inventario</SectionTitle>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors.primary }}>
                        <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                          Producto
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                          Bodega
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: 700 }}>
                          Existencia
                        </TableCell>
                        <TableCell align="right" sx={{ color: "#fff", fontWeight: 700 }}>
                          Mínimo
                        </TableCell>
                        <TableCell align="center" sx={{ color: "#fff", fontWeight: 700 }}>
                          Estado
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {alertasInventario.map((alerta, index) => (
                        <TableRow
                          key={`${alerta.product_id}-${alerta.bodega_codigo}-${alerta.sku}-${index}`}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                color: colors.text,
                                fontSize: 13,
                                fontWeight: 700,
                              }}
                            >
                              {alerta.producto}
                              {alerta.variante
                                ? ` - ${alerta.variante}`
                                : ""}
                            </Typography>

                            <Typography
                              sx={{
                                color: colors.muted,
                                fontSize: 11.5,
                              }}
                            >
                              {alerta.codigo}
                              {alerta.sku ? `-${alerta.sku}` : ""}
                            </Typography>
                          </TableCell>

                          <TableCell>{alerta.bodega}</TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              color: colors.danger,
                              fontWeight: 700,
                            }}
                          >
                            {number(alerta.cantidad)}
                          </TableCell>

                          <TableCell align="right">
                            {number(alerta.stock_minimo)}
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              label={
                                alerta.estado === "AGOTADO"
                                  ? "Agotado"
                                  : "Stock bajo"
                              }
                              size="small"
                              sx={{
                                bgcolor: colors.dangerLight,
                                color: colors.danger,
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

const AdminStatistics = () => {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const { user } = useContext(AppContext);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!user?.isLoggedIn) {
      navigate("/login", { replace: true });
    }
  }, [user?.isLoggedIn, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: colors.background,
      }}
    >
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />
        <AdminStatisticsContent />
      </Box>
    </Box>
  );
};

export default AdminStatistics;